import { Link, type LinkProps, useNavigate } from "@tanstack/react-router";
import { AlertCircle, ClipboardList, LoaderCircle, Lock, UserCircle } from "lucide-react";
import type { ReactNode } from "react";
import { useObraId } from "@/app/useObraId";
import { Alert } from "@/components/ui/alert";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { membresiaEnObra, useMe } from "@/lib/auth";
import {
  type CandadoActivo,
  type EstadoEquipo,
  type LotoReciente,
  useCandadosActivos,
  useKpis,
  useLotosRecientes,
} from "@/lib/dashboard";

// Etiqueta + color de cada estado de LOTO (badge). Ciclo de vida binario (F3, #241). El mapa
// es local a propósito: `features/*` no importa de otra feature.
const ESTADO_LOTO: Record<string, { label: string; className: string }> = {
  ABIERTO: { label: "Abierto", className: "bg-navy-100 text-navy-800" },
  CERRADO: { label: "Cerrado", className: "bg-slate-100 text-slate-600" },
};

// Etiqueta + color del estado calculado de equipo, binario desde #243: BLOQUEADO rojo
// (hay un candado puesto) / DESBLOQUEADO verde.
const ESTADO_EQUIPO: Record<EstadoEquipo, { label: string; className: string }> = {
  BLOQUEADO: { label: "Bloqueado", className: "bg-red-100 text-red-700" },
  DESBLOQUEADO: { label: "Desbloqueado", className: "bg-emerald-100 text-emerald-700" },
};

// formatFecha da un instante ISO en formato local es-ES (día y hora), para las columnas de
// tiempo de los paneles.
function formatFecha(iso: string): string {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) {
    return "—";
  }
  return fecha.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * DashboardPage es el aterrizaje operativo (manual §3): identidad de la sesión, KPIs del
 * estado LOTO en tiempo real, panel de candados activos en planta y últimos LOTOs con el
 * estado real (calculado) de su equipo. Todos los datos vienen del backend (#43), única
 * autoridad; el panel es de solo lectura y visible para cualquier usuario onboarded.
 */
export function DashboardPage() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const obraId = useObraId();
  // Rol del usuario en la obra activa (el admin global opera como "ADMIN"). Se muestra como
  // badge de identidad de la sesión, junto al nombre.
  const rolEnObra = me
    ? (membresiaEnObra(me, obraId)?.rol ?? (me.es_admin ? "ADMIN" : undefined))
    : undefined;
  const kpis = useKpis(obraId);
  const candados = useCandadosActivos(obraId);
  const lotos = useLotosRecientes(obraId);

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <UserCircle className="h-10 w-10 text-slate-400" aria-hidden="true" />
            <div className="flex flex-col">
              <CardTitle>
                {me ? `${me.nombre} ${me.apellidos}`.trim() : "Sesión iniciada"}
              </CardTitle>
              {me ? <span className="text-base text-slate-500">@{me.nombre_usuario}</span> : null}
            </div>
            <div className="flex flex-wrap gap-2 sm:ml-auto">
              {rolEnObra ? (
                <span className="rounded-full bg-navy-100 px-3 py-1 font-semibold text-navy-800 text-sm">
                  {rolEnObra}
                </span>
              ) : null}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* KPIs */}
      <section aria-label="Indicadores">
        {kpis.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>No se pudieron cargar los indicadores. Inténtalo de nuevo.</span>
          </Alert>
        ) : (
          // #429: cada tarjeta lleva a su listado con SOLO su filtro (D1), también mientras
          // carga (D4). «Personal habilitado» salió del inicio (D2): el API aún manda
          // `personal_habilitado`, pero no se pinta. Tres columnas desde `sm` (D7).
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KpiCard
              label="LOTOs abiertos"
              value={kpis.data?.lotos.abierto}
              destino={{
                to: "/obras/$obraId/lotos",
                params: { obraId: String(obraId) },
                search: { estado: "ABIERTO" },
              }}
            />
            <KpiCard
              label="LOTOs cerrados"
              value={kpis.data?.lotos.cerrados}
              destino={{
                to: "/obras/$obraId/lotos",
                params: { obraId: String(obraId) },
                search: { estado: "CERRADO" },
              }}
            />
            <KpiCard
              label="Equipos bloqueados"
              value={kpis.data?.equipos_bloqueados}
              accent="danger"
              destino={{
                to: "/obras/$obraId/equipos",
                params: { obraId: String(obraId) },
                search: { estado: "BLOQUEADO" },
              }}
            />
          </div>
        )}
      </section>

      {/* Candados activos en planta */}
      <section className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Lock className="h-5 w-5 text-red-600" aria-hidden="true" />
          <h2 className="font-bold text-slate-900 text-xl tracking-tight">
            Candados activos en planta
          </h2>
        </div>
        <PanelEstado
          query={candados}
          errorMsg="No se pudieron cargar los candados activos."
          vacio="No hay candados colocados en planta."
          render={(filas) => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Área</TableHead>
                  <TableHead>Equipo</TableHead>
                  <TableHead>Puntos</TableHead>
                  <TableHead>Candado</TableHead>
                  <TableHead className="hidden sm:table-cell">Operario</TableHead>
                  <TableHead className="hidden sm:table-cell">Colocado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filas.map((c: CandadoActivo) => (
                  <TableRow
                    key={c.id_bloqueo}
                    className="cursor-pointer hover:bg-primary-50/60"
                    onClick={() =>
                      navigate({
                        to: "/obras/$obraId/lotos/$id",
                        params: { obraId: String(obraId), id: String(c.id_loto) },
                      })
                    }
                  >
                    <TableCell>{c.area}</TableCell>
                    <TableCell>
                      <span className="font-medium text-slate-900">{c.equipo}</span>
                      <span className="block text-slate-500 text-sm">{c.codigo_equipo}</span>
                    </TableCell>
                    <TableCell>{c.puntos || "—"}</TableCell>
                    <TableCell className="tabular-nums">
                      <Link
                        to="/obras/$obraId/lotos/$id"
                        params={{ obraId: String(obraId), id: String(c.id_loto) }}
                        className="font-medium text-primary-700 tabular-nums hover:underline"
                      >
                        {c.codigo_candado}
                      </Link>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">{c.operario || "—"}</TableCell>
                    <TableCell className="hidden text-slate-500 sm:table-cell">
                      {formatFecha(c.fecha_colocacion)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        />
      </section>

      {/* LOTOs recientes */}
      <section className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-primary-600" aria-hidden="true" />
          <h2 className="font-bold text-slate-900 text-xl tracking-tight">LOTOs recientes</h2>
        </div>
        <PanelEstado
          query={lotos}
          errorMsg="No se pudieron cargar los LOTOs recientes."
          vacio="Aún no hay LOTOs registrados."
          render={(filas) => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº LOTO</TableHead>
                  <TableHead>Equipo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Equipo (estado)</TableHead>
                  <TableHead className="hidden sm:table-cell">Apertura</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filas.map((l: LotoReciente) => (
                  <TableRow
                    key={l.id}
                    className="cursor-pointer hover:bg-primary-50/60"
                    onClick={() =>
                      navigate({
                        to: "/obras/$obraId/lotos/$id",
                        params: { obraId: String(obraId), id: String(l.id) },
                      })
                    }
                  >
                    <TableCell className="tabular-nums">
                      <Link
                        to="/obras/$obraId/lotos/$id"
                        params={{ obraId: String(obraId), id: String(l.id) }}
                        className="font-medium text-primary-700 tabular-nums hover:underline"
                      >
                        {l.numero_loto}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-slate-900">{l.equipo}</span>
                      <span className="block text-slate-500 text-sm">{l.area}</span>
                    </TableCell>
                    <TableCell>
                      <EstadoLotoBadge estado={l.estado} />
                    </TableCell>
                    <TableCell>
                      <EstadoEquipoBadge estado={l.estado_equipo} />
                    </TableCell>
                    <TableCell className="hidden text-slate-500 sm:table-cell">
                      {formatFecha(l.fecha_apertura)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        />
      </section>
    </div>
  );
}

/**
 * KpiCard muestra un indicador: valor grande + etiqueta, con acento opcional de peligro. La
 * tarjeta ENTERA es un único enlace a su listado filtrado (#429, D3), con el diseño de siempre
 * más el estado de interacción (D5); su nombre accesible es «valor etiqueta» (D6).
 */
function KpiCard({
  label,
  value,
  accent,
  destino,
}: {
  label: string;
  value: number | undefined;
  accent?: "danger";
  destino: LinkProps;
}) {
  const danger = accent === "danger" && typeof value === "number" && value > 0;
  return (
    <Link
      {...destino}
      className="flex flex-col gap-1 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
    >
      <span
        className={`font-bold text-3xl tabular-nums tracking-tight ${
          danger ? "text-red-700" : "text-slate-900"
        }`}
      >
        {typeof value === "number" ? value : "—"}
      </span>
      <span className="font-medium text-slate-500 text-sm">{label}</span>
    </Link>
  );
}

/**
 * PanelEstado envuelve un panel de tabla con los estados de carga/error/vacío comunes, para
 * no repetirlos en cada panel del dashboard.
 */
function PanelEstado<T>({
  query,
  errorMsg,
  vacio,
  render,
}: {
  query: { data?: T[]; isPending: boolean; isError: boolean };
  errorMsg: string;
  vacio: string;
  render: (filas: T[]) => ReactNode;
}) {
  if (query.isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <span>{errorMsg}</span>
      </Alert>
    );
  }
  if (query.isPending) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
        <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
        Cargando…
      </div>
    );
  }
  if (!query.data || query.data.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-12 text-center text-slate-500">
        {vacio}
      </div>
    );
  }
  return render(query.data);
}

function EstadoLotoBadge({ estado }: { estado: string }) {
  const info = ESTADO_LOTO[estado] ?? { label: estado, className: "bg-slate-100 text-slate-600" };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-semibold text-xs ${info.className}`}
    >
      {info.label}
    </span>
  );
}

function EstadoEquipoBadge({ estado }: { estado: EstadoEquipo }) {
  const info = ESTADO_EQUIPO[estado];
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-semibold text-xs ${info.className}`}
    >
      {info.label}
    </span>
  );
}
